// src/pages/PrivacyPolicy.js
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

export default function PrivacyPolicy() {
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.background }}>

      {/* Simple header */}
      <div className="px-6 py-5 flex items-center justify-between max-w-3xl mx-auto">
        <Link to="/">
          <img src="/logo.png" alt="TripData" className="h-8 object-contain" />
        </Link>
        <Link
          to="/"
          className="text-sm transition-colors hover:opacity-70"
          style={{ color: theme.textSecondary }}
        >
          ← Back to site
        </Link>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-20">
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.text }}>Privacy Policy</h1>
        <p className="text-sm mb-10" style={{ color: theme.textMuted }}>Last updated: 30 March 2026</p>

        <p className="mb-8" style={{ color: theme.textSecondary }}>
          TripData is developed and operated by CodeBooth (Pty) Ltd. This privacy policy explains how
          we collect, use, and protect your information when you use the TripData mobile application.
        </p>

        <Section title="Information We Collect" theme={theme}>
          <ul className="space-y-3">
            <Li theme={theme}><strong>Location data</strong> — TripData collects GPS location data continuously in the background while the app is active. This is required to detect and record your vehicle trips. Location data is stored on our secure servers and used solely for trip tracking and logbook generation.</Li>
            <Li theme={theme}><strong>Account information</strong> — Your name, email address, and password (encrypted) when you register.</Li>
            <Li theme={theme}><strong>Vehicle information</strong> — Vehicle details you enter manually.</Li>
            <Li theme={theme}><strong>Trip data</strong> — Routes, distances, addresses, and classifications you assign to trips.</Li>
            <Li theme={theme}><strong>Expense data</strong> — Fuel, toll, service, and other vehicle expenses you log manually.</Li>
            <Li theme={theme}><strong>Device information</strong> — Device type and push notification tokens for app notifications.</Li>
          </ul>
        </Section>

        <Section title="How We Use Your Information" theme={theme}>
          <ul className="space-y-3">
            <Li theme={theme}>To record and display your trip history</Li>
            <Li theme={theme}>To generate SARS-compliant logbook reports</Li>
            <Li theme={theme}>To track vehicle expenses and calculate cost per kilometre</Li>
            <Li theme={theme}>To send trip-related push notifications</Li>
            <Li theme={theme}>To manage your account</Li>
          </ul>
        </Section>

        <Section title="Data Storage" theme={theme}>
          <p style={{ color: theme.textSecondary }}>
            Your data is stored on secure servers located in South Africa. We do not sell or share
            your personal information with third parties. Trip data is retained for as long as your
            account is active. You may request deletion of your account and all associated data at
            any time.
          </p>
        </Section>

        <Section title="Payment Information" theme={theme}>
          <p style={{ color: theme.textSecondary }}>
            TripData does not process or store any payment information within the app. Plan management
            and payments are handled securely via our website at{' '}
            <a href="https://tripdata.co.za" className="hover:opacity-70" style={{ color: theme.primary }}>
              tripdata.co.za
            </a>.
          </p>
        </Section>

        <Section title="Third-Party Services" theme={theme}>
          <ul className="space-y-3">
            <Li theme={theme}><strong>Google Maps</strong> — reverse geocoding for address lookup</Li>
            <Li theme={theme}><strong>Expo / Firebase</strong> — push notifications</Li>
            <Li theme={theme}><strong>Apple / Google</strong> — in-app purchase processing</Li>
          </ul>
        </Section>

        <Section title="Your Rights" theme={theme}>
          <p style={{ color: theme.textSecondary }}>
            You may request deletion of your account and all associated data by contacting us at{' '}
            <a href="mailto:hello@codebooth.co.za" className="hover:opacity-70" style={{ color: theme.primary }}>
              hello@codebooth.co.za
            </a>.
          </p>
        </Section>

        <Section title="Contact" theme={theme}>
          <p style={{ color: theme.textSecondary }}>
            CodeBooth (Pty) Ltd<br />
            <a href="mailto:hello@codebooth.co.za" className="hover:opacity-70" style={{ color: theme.primary }}>
              hello@codebooth.co.za
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children, theme }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-4" style={{ color: theme.text }}>{title}</h2>
      {children}
    </div>
  );
}

function Li({ children, theme }) {
  return (
    <li className="flex gap-3 text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
      <span style={{ color: theme.primary }} className="mt-0.5 shrink-0">•</span>
      <span>{children}</span>
    </li>
  );
}